import React from "react";
import {observer} from "mobx-react";

import "./station-popup.scss";

import {Button, Card, Icon, StrictTabProps} from "semantic-ui-react";
import {Rnd} from "react-rnd";

import {MapState} from "../../../core/map-state";
import {UiState} from "../../../core/ui-state";

import {ChartConfiguration} from "../../../config/chart-configuration";
import {StationPopupConfiguration} from "../../../config/station-popup-configuration";
import {Kiwis} from "../../../kiwis";

import {StationTabs} from "./station-tabs/station-tabs";
import {WindowDimensions, WindowDimensionsProps} from "../../../utils/window-dimensions";
import {Online} from "./online/online";

interface StationPopupProps {
    mapStore: MapState;
    uiStore: UiState;
    kiwis: Kiwis;
    config: StationPopupConfiguration;
    chartConfig: ChartConfiguration;
}

interface CloseButtonProps {
    onClick: any;
}

const CloseButton = (props: CloseButtonProps) => (
    <Button icon size={"small"}
        floated={"right"}
        className={"geo-station-popup-close-button"}
        onClick={props.onClick}>
        <Icon name="close"/>
    </Button>
);

interface ExpandButtonProps {
    onClick: any;
    isFullscreen: boolean;
}

const ExpandButton = (props: ExpandButtonProps) => (
    <Button icon size={"small"}
        floated={"right"}
        className={"geo-station-popup-expand-button"}
        onClick={props.onClick}>
        <Icon name={props.isFullscreen ? "compress" : "expand"}/>
    </Button>
);

interface DockButtonProps {
    onClick: any;
    dockPosition: DockPosition;
}

const DockButton = (props: DockButtonProps) => (
    <Button icon size="small"
        floated="right"
        className="geo-station-popup-expand-button"
        onClick={props.onClick}>
        <Icon name={props.dockPosition === DockPosition.BottomLeft ? "window restore outline" : "window minimize"}/>
    </Button>
);

export enum DockPosition {
    TopLeft = "docked-top-left",
    TopRight = "docked-top-right",
    BottomLeft = "docked-bottom-left",
    BottomRight = "docked-bottom-right",
    None = ""
}

export enum WindowMode {
    Draggable = "draggable",
    Fullscreen = "fullscreen",
    Docked = "docked"
}

@observer
export class StationPopup extends React.Component<StationPopupProps, any> {
    xMargin = 40;
    yMargin = 70;
    fullscreenWindow = new WindowDimensions({ width: "100%", height: "100%", x: 0, y: 0 });
    dockedWindow = new WindowDimensions({  width: "100%", height: 320, x: 0, y: 0 });
    breakToFixed = 510;  // break point where to switch to fixed mode
    dnd!: Rnd;

    constructor(props: StationPopupProps) {
        super(props);
        this.state = {
            extendedTitle: null,
            updating: false,
            resizing: false, // flag is used to hide content on resizing --> css class 'resizing' is set while resizing
            visible: false,
            originalWindow: this.isFixed() ? this.dockedWindow : null, // to store dimensions to restore from docked or fullscreen mode
            currentMode: this.isFixed() ? WindowMode.Docked : WindowMode.Draggable,
            previousMode: null, // to store previous window mode
            ...this.initialDimensions(props)
        };

        this.updatePopupTitle = this.updatePopupTitle.bind(this);
        this.handleDragStop = this.handleDragStop.bind(this);
        this.handleResizeStart = this.handleResizeStart.bind(this);
        this.handleResizeStop = this.handleResizeStop.bind(this);
        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.toggleFullScreen = this.toggleFullScreen.bind(this);
        this.handleDock = this.handleDock.bind(this);
        this.hide = this.hide.bind(this);
        this.refDnD = this.refDnD.bind(this);
        this.onTabChange = this.onTabChange.bind(this);
    }

    isFixed() {
        return window.innerWidth < this.breakToFixed;
    }

    get isOpen() {
        return this.state.visible;
    }

    initialWidth(width: number) {
        if (this.isFixed()) {
            return this.dockedWindow.width;
        }
        return width + 30  < window.innerWidth ? width : window.innerWidth - 30;
    }
    initialHeight(height: number) {
        if (this.isFixed()) {
            return this.dockedWindow.height;
        }
        return height + 100  < window.innerHeight ? height : window.innerHeight - 100;
    }
    initialDimensions(props: StationPopupProps) {
        const {isSidebarVisible} = this.props.uiStore;
        const xPos = 324; // right of sidebar or menu
        const yPos = isSidebarVisible ? 32 : 4;
        return {
            x: this.isFixed() ? this.dockedWindow.x : xPos,
            y: this.isFixed() ? this.dockedWindow.y : yPos,
            width: this.initialWidth(props.config.width),
            height: this.initialHeight(props.config.height),
            minWidth: props.config.minWidth,
            minHeight: props.config.minHeight,
            fixedMinHeight: props.config.fixedMinHeight,
            dockPosition: this.isFixed() ? DockPosition.BottomLeft : DockPosition.None, // DockPosition
            fixed: this.isFixed(), // used to fix on smaller screens
            fixedByResize: this.isFixed(),
            aspectRatio: 1
        };
    }

    toggleFullScreen(){
        this.setState((state: any) => {
            let dimension: WindowDimensionsProps;
            let mode: any;
            if (state.currentMode !== WindowMode.Fullscreen) {
                if (state.currentMode !== WindowMode.Docked) {
                    // remember original 'normal' dimensions
                    state.originalWindow = new WindowDimensions({
                        width: state.width,
                        height: state.height,
                        x: state.x,
                        y: state.y
                    });
                }

                dimension = this.fullscreenWindow.dimensions;
                mode = { currentMode: WindowMode.Fullscreen };
            } else {
                if (state.previousMode === WindowMode.Docked) {
                    // go back to docked
                    dimension = this.dockedWindow.dimensions;
                } else {
                    // go back to normal
                    dimension = state.originalWindow.dimensions;
                }
                mode = { currentMode: state.previousMode };
            }
            return {
                previousMode: state.currentMode,
                ...mode,
                ...dimension
            };
        });
    }

    toggleVisibility() {
        this.setState({
            visible: !this.state.visible
        });
    }

    hide() {
        this.setState(() => ({
            visible: false
        }));
        this.props.mapStore.unhighlightFeature();
        this.props.uiStore.unlockPopup();
    }

    show() {
        // do not show standard popup at the same time
        // event to open standard popup is triggered before this
        this.props.uiStore.lockPopup();

        this.setState(() => ({
            visible: true,
            extendedTitle: null
        }));
    }

    handleDock() {
        if (this.state.currentMode !== WindowMode.Docked) {
            this.dock();
        } else {
            this.undock();
        }
    }

    dock() {
        this.setState((state: any) => {
            if (state.currentMode === WindowMode.Docked) {
                return;
            }
            if (state.currentMode !== WindowMode.Fullscreen) {
                // remember original 'normal' dimensions
                state.originalWindow = new WindowDimensions({
                    width: state.width,
                    height: state.height,
                    x: state.x,
                    y: state.y
                });
            }

            return {
                currentMode: WindowMode.Docked,
                previousMode: state.currentMode,
                dockPosition: DockPosition.BottomLeft,
                ...this.dockedWindow.dimensions
            };
        });
    }

    undock() {
        this.setState((state: any) => {
            if (state.currentMode === WindowMode.Draggable) {
                return;
            }
            // go back to draggable and original size and position
            return {
                currentMode: WindowMode.Draggable,
                previousMode: WindowMode.Docked,
                dockPosition: DockPosition.None,
                ...state.originalWindow.dimensions
            };
        });
    }

    fix(byResize: boolean) {
        this.setState({
            fixed: true,
            fixedByResize: byResize
        });
    }

    free() {
        this.setState({
            fixed: false,
            fixedByResize: false
        });
    }

    get hasSpaceForExtendedTitle() {
        let w = this.state.width;
        if (w === "100%") {
            // full screen and top menu
            w = window.innerWidth;
        }
        // space available
        return w >= 620;
    }

    get hasSpaceForStationNumber() {
        let w = this.state.width;
        if (w === "100%") {
            // full screen and top menu
            w = window.innerWidth;
        }
        return w > 500;
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleWindowResize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleWindowResize);
    }

    updatePopupTitle(title: string) {
        this.setState({
            extendedTitle: title
        });
    }

    handleDragStop(e: any, data: any) {
        this.setState({ x: data.x, y: data.y, fixed: false });
        e.preventDefault();
    }

    refDnD(r: Rnd | null) {
        if (!r) { return; }
        this.dnd = r;
    }

    /**
     * Set popup window height
     *
     * @param height
     */
    setHeight(height: number) {
        const h = height - this.yMargin;
        this.setState((state: any) => ({
            height: h,
            aspectRatio: state.width / h
        }));
    }

    /**
     * Set popup window width
     *
     * @param width
     */
    setWidth(width: number) {
        const w = width - this.xMargin;
        this.setState((state: any) => ({
            width: w,
            aspectRatio: w / state.height
        }));
    }

    /**
     * Update aspect ratio when window width has changed
     *
     * @param width
     */
    setAspectRatioWidth(width: number) {
        this.setState((state: any) => {
            let h = state.height;
            if (state.height === "100%") {
                h = 100;
            }
            return {
                aspectRatio: width / h
            };
        });
    }

    /**
     * Update aspect ratio when window height has changed
     *
     * @param height
     */
    setAspectRatioHeight(height: number) {
        this.setState((state: any) => {
            let w = state.width;
            if (state.width === "100%") {
                w = 100;
            }
            return {
                aspectRatio: w / height
            };
        });
    }

    handleResizeStart() {
        if (this.state.fixed) {
            return;
        }
        // mark as resizing to hide content while resizing
        this.props.uiStore.startResizing();
    }

    /**
     * Handle resize of drag and drop popup window
     *
     * @param e
     * @param direction
     * @param ref
     * @param delta
     * @param position
     */
    handleResizeStop(e: any, direction: any, ref: any, delta: any, position: any) {
        e.preventDefault();
        if (this.state.fixed) {
            return;
        }

        const w = parseInt(ref.style.width, 10);
        const h = parseInt(ref.style.height, 10);
        this.setState({
            width: w,
            height: h,
            windowResizeIntersectWidth: false,
            windowResizeIntersectHeight: false,
            aspectRatio: w / h,
            ...position,
        });
        // remove resizing flag to show content again
        this.props.uiStore.stopResizing();
    }

    /**
     * Handle dimension changes of browser window
     */
    handleWindowResize() {
        if (window.innerWidth < this.breakToFixed) {
            if (!this.state.fixed && this.state.currentMode !== WindowMode.Fullscreen) {
                this.fix(true);
                this.dock();
            }
        } else {
            if (this.state.fixed && this.state.fixedByResize) {
                this.free();
                this.undock();
            }
        }
        if (this.state.fixed) {
            return;
        }

        if (this.state.width === "100%") {
            // full screen, when resizing browser window
            this.setAspectRatioWidth(window.innerWidth);
        } else if (window.innerWidth < this.state.width) {
            this.setWidth(window.innerWidth);

            // remember the original width
            if (!this.state.windowResizeIntersectWidth) {
                this.setState((state: any) => ({
                    windowResizeIntersectWidth: true,
                    widthOnIntersect: state.width
                }));
            }
        } else if (window.innerWidth > (this.state.width + this.xMargin) &&
            this.state.windowResizeIntersectWidth) {
            // reset original width
            // browser window growing but still smaller than original size
            this.setWidth(window.innerWidth);

            if (window.innerWidth > this.state.widthOnIntersect + this.xMargin) {
                // original size fits to browser window again
                this.setState({ windowResizeIntersectWidth: false });
            }
        }

        if (this.state.height === "100%") {
            // full screen, when resizing browser window
            this.setAspectRatioHeight(window.innerHeight);
        } else if (window.innerHeight < this.state.height) {
            this.setHeight(window.innerHeight);

            // remember the original height
            if (!this.state.windowResizeIntersectHeight) {
                this.setState((state: any) => ({
                    windowResizeIntersectHeight: true,
                    heightOnIntersect: state.height
                }));
            }
        } else if (window.innerHeight > (this.state.height + this.yMargin) &&
            this.state.windowResizeIntersectHeight) {
            // reset original height
            // browser window growing but still smaller than original size
            this.setHeight(window.innerHeight);

            if (window.innerHeight > this.state.heightOnIntersect + this.yMargin) {
                // original size fits to browser window again
                this.setState({ windowResizeIntersectHeight: false });
            }
        }
    }

    /**
     * Handle tab changes
     *
     * @param event
     * @param data
     */
    onTabChange(event: React.MouseEvent<HTMLDivElement>, data: StrictTabProps) {
        if (data) {
            const station = this.props.kiwis.selectedStation();
            if (!station) {
                return;
            }

            const newTab = Number(data.activeIndex);
            if (isNaN(newTab)) {
                return;
            }

            const activeTab = this.props.uiStore.selectedTab(station);
            if (data.panes) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const previousTs = data.panes[activeTab].pane.props.timeSeries;
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const newTs = data.panes[newTab].pane.props.timeSeries;
                this.props.kiwis.updateVisibleTimeSeries(previousTs, newTs);
                if (newTs) {
                    this.props.kiwis.selectTimeSeries(newTs.id);
                    this.updatePopupTitle(newTs.stationParameterName);
                } else {
                    this.updatePopupTitle("");
                }
            }

            this.props.uiStore.selectTab(station, newTab);
        }
    }

    changeTab(index: number) {
        const station = this.props.kiwis.selectedStation();
        if (!station) {
            return;
        }

        if (this.props.uiStore.hasSelectedTab(station)) {
            // There is a selected tab for this station, respect users choice
            return;
        }
        this.props.uiStore.selectTab(station, index);
    }

    render() {
        const {mapStore, uiStore, kiwis, chartConfig, config} = this.props;
        if (!mapStore.view) {
            return null;
        }

        if (!uiStore.widgets.has("stationPopup")) {
            uiStore.addWidget("stationPopup", this);
        }

        const {visible, currentMode, width, height, aspectRatio, fixedByResize, dockPosition, fixed} = this.state;
        const isResizing = this.props.uiStore.isResizing();

        if (!visible) {
            return null;
        }

        const isDraggable = currentMode === WindowMode.Draggable;
        const isDocked = currentMode === WindowMode.Docked;
        const isFullscreen = currentMode === WindowMode.Fullscreen;
        const station = kiwis.selectedStation();
        let content;
        if (!station) {
            // no station selected
            content = <span className={"info"}>Station auswählen...</span>;
        } else if (kiwis.timeSeriesList) {
            content = <StationTabs kiwis={kiwis}
                chartConfig={chartConfig}
                stationPopupConfig={config}
                station={station}
                parentWidth={width}
                parentHeight={height}
                aspectRatio={aspectRatio}
                activeTab={uiStore.selectedTab(station)}
                onTabChange={this.onTabChange}
                isFullScreen={isFullscreen}
                isDocked={isDocked}/>;
        } else {
            // time series not yet loaded
            content = <span className={"info"}>Verfügbare Zeitreihen werden geladen...</span>;
        }

        let expandButton;
        if (!isDocked) {
            expandButton = (
                <ExpandButton
                    onClick={(event: any) => {
                        event.preventDefault();
                        this.toggleFullScreen();
                    }}
                    isFullscreen={isFullscreen}
                />
            );
        }

        let dockButton;
        if (window.innerWidth >= this.breakToFixed && !fixedByResize) {
            // on smaller screens, the popup cannot be undocked
            dockButton = (
                <DockButton
                    dockPosition={dockPosition}
                    onClick={(event: any) => {
                        event.preventDefault();
                        this.handleDock();
                    }}
                />
            );
        }

        const card = (
            <Card key={`station-card-${station ? station.id : "-"}`} fluid={true}>
                <Card.Content>
                    <Card.Header className={"geo-station-popup-header"}>
                        <div className={`geo-station-popup-title geo-station-popup-drag-handle ${isDraggable ? "draggable" : ""}`}>
                            {station ? station.name : "-"}
                            {this.hasSpaceForStationNumber &&
                            <div className="geo-station-popup-title-station-number">
                                {station ? `(${station.no})` : "-"}
                            </div>
                            }
                            {this.hasSpaceForExtendedTitle && this.state.extendedTitle &&
                            <div className="geo-station-popup-extended-title">
                                {this.state.extendedTitle}
                            </div>
                            }
                        </div>
                        {station &&
                            <Online online={station.online === "yes"}/>
                        }
                        {dockButton}
                        {expandButton}
                        <CloseButton onClick={(event: any) => {
                            event.preventDefault();
                            this.hide();
                        }}/>
                    </Card.Header>
                </Card.Content>
                <Card.Content className={isResizing ? "resizing" : ""}>
                    {content}
                    {(!fixed && !isFullscreen && !isDocked) &&
                        <div className="resize-grip"/>
                    }
                </Card.Content>
            </Card>
        );

        /**
         * Add CSS classes to surrounding div in Fullscreen and Docked mode to disable drag functionality
         * Add CSS to Rnd in Draggable mode
         * This avoids rerendering of content when switching from Draggable to Fullscreen/Docked mode
         */
        const css = "geo-station-popup geo-component geo-widget " +
            `geo-popup ${dockPosition} geo-popup--shadow`;

        const {x, y, minWidth, minHeight} = this.state;

        return (
            <div className={!isDraggable ? css : ""}
                style={!isDraggable ? {
                    bottom: y,
                    left: x,
                    width,
                    height,
                    position: "absolute"
                } : undefined }
            >
                <Rnd
                    ref={this.refDnD}
                    className={isDraggable ? css : ""}
                    size={{width, height}}
                    minWidth={isDraggable ? minWidth : undefined}
                    minHeight={isDraggable ? minHeight : undefined}
                    position={{x, y}}
                    enableResizing={!isDraggable ? {
                        top: false, right: false, bottom: false, left: false, topRight: false,
                        bottomRight: false, bottomLeft: false, topLeft: false
                    } : undefined}
                    disableDragging={!isDraggable}
                    onDragStop={isDraggable ? this.handleDragStop : undefined}
                    onResizeStart={this.handleResizeStart}
                    onResizeStop={this.handleResizeStop}
                    bounds={".geo-ui-root"}
                    dragHandleClassName={"geo-station-popup-drag-handle"}
                >
                    {card}
                </Rnd>
            </div>
        );

    }
}
